<script setup>
import { computed } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import BaseBubble from 'dashboard/components-next/message/bubbles/Base.vue';
import {
  MESSAGE_STATUS,
  MESSAGE_TYPES,
  MESSAGE_VARIANTS,
  ORIENTATION,
  SENDER_TYPES,
} from 'dashboard/components-next/message/constants';
import { provideMessageContext } from 'dashboard/components-next/message/provider';
import ScheduledWorkflowActionPreview from './ScheduledWorkflowActionPreview.vue';

const props = defineProps({
  workflow: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const formattedScheduledAt = computed(() =>
  format(new Date(props.workflow.scheduled_at), 'yyyy-MM-dd HH:mm')
);
const creatorName = computed(
  () =>
    props.workflow.created_by?.name ||
    t('CONVERSATION.SCHEDULED_WORKFLOWS.TIMELINE.UNKNOWN_CREATOR')
);
const creator = computed(() => ({
  id: props.workflow.created_by?.id,
  name: creatorName.value,
  thumbnail: props.workflow.created_by?.thumbnail || '',
  type: SENDER_TYPES.USER,
}));
const creatorTooltip = computed(
  () => `${t('CONVERSATION.SENT_BY')} ${creatorName.value}`
);
const actionCount = computed(() => props.workflow.action_count || 0);
const actionCountLabel = computed(() =>
  actionCount.value === 1
    ? t('CONVERSATION.SCHEDULED_WORKFLOWS.ACTION')
    : t('CONVERSATION.SCHEDULED_WORKFLOWS.ACTIONS')
);
const previewActions = computed(() => props.workflow.preview_actions || []);
const workflowCreatedAt = computed(
  () =>
    new Date(props.workflow.created_at || props.workflow.updated_at).getTime() /
    1000
);

provideMessageContext({
  id: computed(() => props.workflow.id),
  sender: creator,
  senderType: computed(() => SENDER_TYPES.USER),
  variant: computed(() => MESSAGE_VARIANTS.PRIVATE),
  orientation: computed(() => ORIENTATION.RIGHT),
  inReplyTo: computed(() => null),
  shouldGroupWithNext: computed(() => false),
  status: computed(() => MESSAGE_STATUS.SENT),
  isPrivate: computed(() => true),
  createdAt: workflowCreatedAt,
  sourceId: computed(() => ''),
  messageType: computed(() => MESSAGE_TYPES.OUTGOING),
  contentAttributes: computed(() => ({})),
});
</script>

<template>
  <li
    class="scheduled-workflow-timeline-card mb-2 flex w-full list-none justify-end"
  >
    <div
      class="grid grid-cols-[1fr_24px] gap-x-2"
      :style="{
        gridTemplateAreas: `
          'bubble avatar'
          'meta spacer'
        `,
      }"
    >
      <div
        class="[grid-area:bubble] flex min-w-0 justify-end ltr:ml-8 rtl:mr-8"
      >
        <BaseBubble class="px-4 py-3">
          <div class="flex flex-col gap-3">
            <div class="flex items-start gap-2">
              <span
                class="i-ph-clock mt-0.5 size-5 shrink-0 text-n-amber-11"
                aria-hidden="true"
              />
              <div class="min-w-0 flex-1">
                <p class="m-0 text-sm font-semibold">
                  {{ t('CONVERSATION.SCHEDULED_WORKFLOWS.TIMELINE.TITLE') }}
                </p>
                <p class="mb-0 mt-1 text-sm text-n-amber-12">
                  <span class="font-medium">
                    {{
                      t(
                        'CONVERSATION.SCHEDULED_WORKFLOWS.TIMELINE.SCHEDULED_FOR'
                      )
                    }}
                  </span>
                  {{ formattedScheduledAt }}
                </p>
                <p class="mb-0 mt-1 text-xs text-n-amber-11">
                  {{ actionCount }} {{ actionCountLabel }}
                </p>
              </div>
            </div>

            <ScheduledWorkflowActionPreview :actions="previewActions" />
          </div>
        </BaseBubble>
      </div>
      <div
        v-tooltip.left-end="creatorTooltip"
        class="[grid-area:avatar] flex items-end"
        :aria-label="creatorTooltip"
      >
        <Avatar :name="creator.name" :src="creator.thumbnail" :size="24" />
      </div>
    </div>
  </li>
</template>
